import { LineChartView, STRING_DASHED_PROFILE, STRING_FAKE_VALUE_DASHED } from './LineChart.view';
import { AnalyticsContext, SocialProfilesAnalytics } from '../AnalyticsProvider';
import React, { useContext, useMemo } from 'react';
import {
  differenceInDays,
  endOfMonth,
  endOfWeek,
  getDate,
  isSameDay,
  parse,
  startOfDay,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import {
  ConfigChartTicks,
  createTicksChart,
  createTicksConfigs,
  getNormalizedChartPoint,
  getQtyDaysBetweenRange,
  NormalizedChartPoint,
  shouldHideGridLinesEveryFourLines,
  shouldHideGridLinesEverySevenLines,
  verifyWhichTicksShouldRender
} from '../helpers/chartHelpers';
import { DateStringFormat } from 'constants/time-formats';
import { cloneDeep } from 'lodash';
import { StartOfWeekFormatSetting } from '../../../models/graphql';
import { useSettings } from '../../../hooks/useSettings';

export type ChartLinePoint = { [key: string]: number | undefined; normalizedValue?: number | undefined };
type DashedLinePoint = { value: number; indexDay: number };

type DashedLineChartPoint = { firstPoint: DashedLinePoint; lastPoint: DashedLinePoint };
type DashedChartPointsByProfile = {
  [key: string]: DashedLineChartPoint[];
};
type NormalizedChartKeys = Exclude<string, 'day' | 'date' | 'timeInMs'>;

export type ChartLine = {
  index: number;
  day: number;
  date: Date;
  timeInMs: number;
} & {
  [key: NormalizedChartKeys]: NormalizedChartPoint | number | Date;
};

interface FixedProps {
  isEngagementChart: boolean;
  dates: Date[];
  profileIds: string[];
  loading: boolean;
  isEmpty?: boolean;
  ticksConfig?: ConfigChartTicks;
  firstDateFromData: string;
  lastDateFromData: string;
  valueKeyString: string;
  chartTitle: string;
  chartSubTitle: string;
  subTitleTooltipText: string;
  getValuesForChartCb?: () => ChartLinePoint[][];
  valueKeyStringForAxisY: string;
  dateEndPrefillArea?: string;
  dateStartPrefillArea?: string;
  initialFollowers?: { [key: string]: number | null };
}

interface NormalizedChart {
  shouldNormalize: true;
  initialFollowers: { [key: string]: number | null };
}

interface ValueChart {
  shouldNormalize: false;
}

type Charts = NormalizedChart | ValueChart;
type LineChartControllerProps = FixedProps & Charts;
export const getValidProfiles = (profileIdsFromQuery: string[], socialProfiles: SocialProfilesAnalytics[]) => {
  return profileIdsFromQuery.reduce((acc: SocialProfilesAnalytics[], profileId) => {
    const profile = socialProfiles.find(profile => profile.id === profileId);
    if (!profile) {
      return acc;
    }
    return [...acc, profile];
  }, []);
};

export const getHasFillingDataProps = (startDate: Date, endDate: Date, dayWhenWeekStart: StartOfWeekFormatSetting) => {
  const qtyDays = getQtyDaysBetweenRange(startDate, endDate);
  if (qtyDays <= 100) {
    return {
      hasFillingData: false,
      hasPreFillingData: false,
      hasPostFillingData: false
    };
  }
  if (qtyDays >= 367) {
    const hasPreFillingData = !isSameDay(startOfMonth(startDate), startDate);

    const hasPostFillingData = !isSameDay(endOfMonth(endDate), endDate);
    const hasFillingData = hasPreFillingData || hasPostFillingData;

    return {
      hasFillingData,
      hasPreFillingData,
      hasPostFillingData
    };
  }
  const weekStartsOn = dayWhenWeekStart === StartOfWeekFormatSetting.SUNDAY ? 0 : 1;
  const hasPreFillingData = !isSameDay(startOfWeek(startDate, { weekStartsOn }), startDate);

  const hasPostFillingData = !isSameDay(endOfWeek(endDate, { weekStartsOn }), endDate);
  const hasFillingData = hasPreFillingData || hasPostFillingData;

  return {
    hasFillingData,
    hasPreFillingData,
    hasPostFillingData
  };
};

export const createTicksAxisY = (min: number, max: number): number[] => {
  const QNTY_YAXIS_TICK = 5;
  const findDivisor = (range: number) => {
    let divisor = 1000; // Default divisor for very large ranges
    if (range <= 2) divisor = 0.5;
    else if (range <= 5) divisor = 1;
    else if (range <= 10) divisor = 2;
    else if (range <= 25) divisor = 5;
    else if (range <= 50) divisor = 10;
    else if (range <= 100) divisor = 20;
    else if (range <= 200) divisor = 25;
    else if (range <= 400) divisor = 50;
    else if (range <= 2000) divisor = 100;
    return divisor;
  };
  const calculateIncrement = (range: number, divisor: number): number =>
    Math.ceil(range / QNTY_YAXIS_TICK / divisor) * divisor;

  const generateValues = (start: number, increment: number, count: number): number[] => {
    const values = Array.from({ length: count }, (_, currentIndex) => start + currentIndex * increment);
    const firstElement = values[0];
    const lastElement = values[values.length - 1];
    if (lastElement < max) {
      values.push(lastElement + increment);
    }
    if (firstElement > min) {
      const newTickValue = firstElement - increment;
      values.unshift(newTickValue < 0 ? 0 : newTickValue);
    }
    if (values[values.length - 2] >= max) {
      values.pop();
    }
    return values;
  };

  const roundNumber = (number: number) => {
    if (number < 0) {
      return Math.floor(number);
    } else {
      return Math.ceil(number);
    }
  };

  const initialRange = Math.abs(max - min);
  const divisor = findDivisor(initialRange);
  // Calculate effective minimum and adjust it to the nearest multiple of the divisor
  const effectiveMin = roundNumber(min / divisor) * divisor;
  const calculatedRange = Math.abs(max - effectiveMin);
  const effectiveRange = calculatedRange === 0 ? 1 : calculatedRange;

  const increment = calculateIncrement(effectiveRange, divisor);
  return generateValues(effectiveMin, increment, QNTY_YAXIS_TICK);
};

type ReferenceAreaCoordinates = { x1: number; x2: number; y1: number; y2: number };

export type FillingDataConfig = {
  referenceAreaPostFillingCoordinates?: ReferenceAreaCoordinates;
  referenceAreaPreFillingCoordinates?: ReferenceAreaCoordinates;
  hasPreFillingData?: boolean;
  hasPostFillingData?: boolean;
  hasFillingData: boolean;
};

export const getFillingDataConfigs = (
  firstDateFromData: string,
  lastDateFromData: string,
  startDate: Date,
  endDate: Date,
  YAxisMinValue: number,
  YAxisMaxValue: number,
  chartHasInitialFollowers: boolean,
  hasFillingData: boolean,
  hasPreFillingData: boolean,
  hasPostFillingData: boolean,
  dateStartPrefillArea: string | undefined,
  dateEndPrefillArea: string | undefined
): FillingDataConfig => {
  const currentDate = new Date();
  const parsedStartPrefill = parse(dateStartPrefillArea ?? '', DateStringFormat, currentDate);

  const parsedEndFillArea = parse(dateEndPrefillArea ?? '', DateStringFormat, currentDate);
  const dataStartDate = parse(firstDateFromData, DateStringFormat, currentDate);
  const dataEndDate = parse(lastDateFromData, DateStringFormat, currentDate);
  const referenceAreaPreFillingCoordinates = {
    x1: chartHasInitialFollowers ? dataStartDate.getTime() : parsedStartPrefill.getTime(),
    x2: chartHasInitialFollowers ? startDate.getTime() : parsedEndFillArea.getTime(),
    y1: YAxisMinValue,
    y2: YAxisMaxValue
  };
  const referenceAreaPostFillingCoordinates = {
    x1: startOfDay(endDate).getTime(),
    x2: dataEndDate.getTime(),
    y1: YAxisMinValue,
    y2: YAxisMaxValue
  };

  return {
    hasFillingData,
    referenceAreaPostFillingCoordinates,
    referenceAreaPreFillingCoordinates,
    hasPreFillingData,
    hasPostFillingData
  };
};

export const createChartData = (
  dates: Date[],
  profiles: SocialProfilesAnalytics[],
  chartDataPoints: ChartLinePoint[][],
  valueKeyString: string,
  shouldNormalize: boolean
): { chartDataObjs: ChartLine[]; dashedLinesPointsByProfile: DashedChartPointsByProfile } => {
  if (profiles.length !== chartDataPoints.length) {
    return { chartDataObjs: [], dashedLinesPointsByProfile: {} };
  }
  const dashedLinesPointsByProfile: DashedChartPointsByProfile = profiles.reduce((acc, currentProfile) => {
    return { ...acc, [currentProfile.id]: [] };
  }, {});
  const chartDataObjs = dates.map((date, indexDay) => {
    return profiles.reduce(
      (acc, currentProfile, profileIndex) => {
        const currentDayData = chartDataPoints[profileIndex][indexDay];
        const rawValue = currentDayData && currentDayData[valueKeyString];
        const normalizedValue = shouldNormalize ? currentDayData?.normalizedValue : currentDayData[valueKeyString];
        const isLastDay = indexDay + 1 === chartDataPoints[profileIndex].length;

        const chartPointObj: ChartLine = {
          ...acc,
          [currentProfile.id]: {
            rawValue,
            normalizedValue
          }
        };
        if (indexDay !== 0) {
          const { [valueKeyString]: previousRawValue, normalizedValue: previousNormalizedValue } =
            chartDataPoints[profileIndex][indexDay - 1];
          if (rawValue === undefined) {
            if (typeof previousRawValue === 'number') {
              // if previous day is not null and current day is null, this is the first point for the null line
              dashedLinesPointsByProfile[currentProfile.id].push({
                firstPoint: {
                  value: shouldNormalize ? previousNormalizedValue ?? 0 : previousRawValue,
                  indexDay: indexDay
                },
                lastPoint: { value: 0, indexDay } // necessary just because of the type definition
              });
            }
            if (isLastDay) {
              const point = dashedLinesPointsByProfile[currentProfile.id].pop();
              if (point) {
                point.lastPoint = {
                  value: point.firstPoint.value,
                  indexDay
                };
                dashedLinesPointsByProfile[currentProfile.id].push(point);
              }
            }
          }
          // if previous was null and current is not null, this is the last point for the current dashed line
          if (previousRawValue === undefined && typeof normalizedValue === 'number') {
            const currentDashedLine = dashedLinesPointsByProfile[currentProfile.id];

            const point = currentDashedLine.pop();
            if (point) {
              if (chartDataPoints[profileIndex][0].rawValue === null && currentDashedLine.length === 0) {
                // If profile was disconnected in the first day and reconnected, the first point must be equal to reconnected value
                point.firstPoint = { value: normalizedValue ?? rawValue, indexDay: 0 };
              }
              point.lastPoint = { value: normalizedValue ?? rawValue, indexDay: indexDay - 1 };
              dashedLinesPointsByProfile[currentProfile.id].push(point);
            }
          }
        } else if (rawValue === undefined) {
          const firstPointWithValueNotNull = chartDataPoints[profileIndex].find(
            point => typeof point[valueKeyString] === 'number'
          );
          const fakeValueForFirstPointDashedLine = firstPointWithValueNotNull?.[valueKeyString] ?? 0;
          dashedLinesPointsByProfile[currentProfile.id].push({
            firstPoint: {
              value: shouldNormalize ? 0 : fakeValueForFirstPointDashedLine,
              indexDay
            },
            lastPoint: { value: 0, indexDay } // necessary just because of the type definition
          });
        }

        return chartPointObj;
      },
      {
        day: getDate(date),
        date: date,
        timeInMs: date.getTime(),
        index: indexDay
      }
    );
  });
  return { chartDataObjs, dashedLinesPointsByProfile };
};

export const addDashedLineChartsProps = (
  originalChartObj: ChartLine[],
  dashedLinePoints: DashedChartPointsByProfile
) => {
  function calculateInterpolatedValue(
    startDate: number,
    firstValue: number,
    endDate: number,
    lastValue: number,
    dateToFindValue: number
  ) {
    return firstValue + ((lastValue - firstValue) * (dateToFindValue - startDate)) / (endDate - startDate);
  }
  const copyChartObj = cloneDeep(originalChartObj);
  Object.keys(dashedLinePoints).forEach(profileId => {
    const currentProfileDashedPoints = dashedLinePoints[profileId];
    const hasDashedLines = currentProfileDashedPoints.length > 0;
    if (hasDashedLines) {
      currentProfileDashedPoints.forEach(dashedPoint => {
        const { firstPoint, lastPoint } = dashedPoint;
        const keyNameForProfile = `${STRING_DASHED_PROFILE}${profileId}`;

        // We need to create fake points for the dashed lines
        const startIndex = firstPoint.indexDay !== 0 ? firstPoint.indexDay - 1 : firstPoint.indexDay;
        const startDashedLinePoint = copyChartObj[startIndex];
        const endDashedLinePoint = copyChartObj[lastPoint.indexDay];
        const startValueDashedLine =
          getNormalizedChartPoint(startDashedLinePoint[profileId])?.fakeValue ?? firstPoint.value;
        const endValueDashedLine = getNormalizedChartPoint(endDashedLinePoint[profileId])?.fakeValue ?? lastPoint.value;

        // This will populate the dashed line from the first point that we dont have data
        for (let index = firstPoint.indexDay; index <= lastPoint.indexDay; index++) {
          const chartLine = copyChartObj[index];
          const value = calculateInterpolatedValue(
            startDashedLinePoint.timeInMs,
            startValueDashedLine,
            endDashedLinePoint.timeInMs,
            endValueDashedLine,
            chartLine.timeInMs
          );
          if (index === firstPoint.indexDay) {
            Object.assign(chartLine, {
              [keyNameForProfile]: { value, [STRING_FAKE_VALUE_DASHED]: value }
            });
          } else {
            Object.assign(chartLine, {
              [keyNameForProfile]: { value }
            });
          }
        }
        // We also need draw a fake dashed line to connect the point with date with the start of the dashed line if the chart does not start with dashed line
        if (firstPoint.indexDay !== 0) {
          Object.assign(startDashedLinePoint, {
            [keyNameForProfile]: { [STRING_FAKE_VALUE_DASHED]: startValueDashedLine }
          });
        }
      });
    }
  });
  return copyChartObj;
};

const getMinMaxValue = (chartPoints: ChartLinePoint[][], chartValueKeyString: string) => {
  const filteredPoints = chartPoints.flat().filter(point => point[chartValueKeyString] !== undefined);
  const chartValues = filteredPoints.map(point => point[chartValueKeyString] ?? 0);
  if (chartValues.length === 0) {
    return { minValue: 0, maxValue: 0 };
  }
  const maxValue = Math.max(...chartValues);
  const minValue = Math.min(...chartValues);
  return { minValue, maxValue };
};

const makeFakePoints = (
  chartData: ChartLine[],
  profileIds: string[],
  hasPrefill: boolean,
  hasPostFill: boolean,
  isNormalized: boolean
): ChartLine[] => {
  const getFillingPointWithFakeValue = (
    pointBeforeFillingData: ChartLine,
    pointToFindTheFakeValue: ChartLine,
    pointAfterFillingData: ChartLine
  ) => {
    return profileIds.reduce<ChartLine>((currentObj, profileId) => {
      const valuesFillingDay = getNormalizedChartPoint(pointToFindTheFakeValue[profileId]);
      if (valuesFillingDay?.rawValue === undefined) {
        return currentObj;
      }
      if (isNormalized && typeof valuesFillingDay?.normalizedValue === 'number') {
        return { ...currentObj, [profileId]: { ...valuesFillingDay, fakeValue: valuesFillingDay.normalizedValue } };
      }
      const valuesBeforeFillingDay = getNormalizedChartPoint(pointBeforeFillingData[profileId]);
      const valuesAfterDay = getNormalizedChartPoint(pointAfterFillingData[profileId]);

      if (!valuesBeforeFillingDay?.rawValue || !valuesAfterDay?.rawValue) {
        if (valuesFillingDay) {
          return {
            ...currentObj,
            [profileId]: {
              ...valuesFillingDay,
              fakeValue: isNormalized ? valuesFillingDay.normalizedValue : valuesFillingDay.rawValue
            }
          };
        } else {
          return currentObj;
        }
      }

      const totalDays = differenceInDays(pointBeforeFillingData.date, pointAfterFillingData.date);
      const qntyDaysFromStart = differenceInDays(pointBeforeFillingData.date, pointToFindTheFakeValue.date);
      const firstBaseValue = isNormalized ? valuesBeforeFillingDay.normalizedValue : valuesBeforeFillingDay.rawValue;
      const secondBaseValue = isNormalized ? valuesAfterDay.normalizedValue : valuesAfterDay.rawValue;

      if (typeof firstBaseValue !== 'number' || typeof secondBaseValue !== 'number') {
        return currentObj;
      }

      const dayPercentage = qntyDaysFromStart / totalDays;
      const unRoundedNumber = firstBaseValue + dayPercentage * (secondBaseValue - firstBaseValue); // equation to find a point inside the line
      const fakeChartValue = Math.round((unRoundedNumber + Number.EPSILON) * 100) / 100;

      return { ...currentObj, [profileId]: { ...valuesFillingDay, fakeValue: fakeChartValue } };
    }, pointToFindTheFakeValue);
  };
  const sizeChart = chartData.length;
  return chartData.map((chartLine, index) => {
    if (hasPrefill && index === 1) {
      return getFillingPointWithFakeValue(chartData[0], chartData[1], chartData[2]);
    } else if (hasPostFill && index === sizeChart - 2) {
      return getFillingPointWithFakeValue(chartData[sizeChart - 3], chartData[sizeChart - 2], chartData[sizeChart - 1]);
    } else {
      return profileIds.reduce<ChartLine>((currentLine, profileId) => {
        const values = getNormalizedChartPoint(currentLine[profileId]);
        if (!values) {
          return currentLine;
        }
        return {
          ...currentLine,
          [profileId]: { ...values, fakeValue: isNormalized ? values.normalizedValue : values.rawValue }
        };
      }, chartLine);
    }
  });
};

const addsPointOnPreviousDay = (chartData: ChartLine[], dashedLinePoints: DashedChartPointsByProfile) => {
  // We need to make a straight line on the previous point whenever we have data on the current point
  const copyChartData = cloneDeep(chartData);
  // This will connect the lines with dashed lines
  Object.keys(dashedLinePoints).forEach(profileId => {
    const currentProfileDashedPoints = dashedLinePoints[profileId];
    const hasDashedLines = currentProfileDashedPoints.length > 0;
    if (hasDashedLines) {
      currentProfileDashedPoints.forEach(dashedPoint => {
        const { lastPoint } = dashedPoint;
        const chartPoint = getNormalizedChartPoint(copyChartData[lastPoint.indexDay][profileId]);

        if (!chartPoint) {
          return;
        }
        const nextDayPoint = copyChartData[lastPoint.indexDay + 1];

        if (nextDayPoint) {
          const nextPoint = getNormalizedChartPoint(nextDayPoint[profileId]);
          if (nextPoint) {
            chartPoint[STRING_FAKE_VALUE_DASHED] = lastPoint.value;
            nextPoint[STRING_FAKE_VALUE_DASHED] = nextPoint?.fakeValue;
          }
        }
      });
    }
  });

  return copyChartData;
};
export const LineChartController = ({
  dates,
  profileIds,
  firstDateFromData,
  lastDateFromData,
  loading,
  isEmpty,
  initialFollowers,
  valueKeyString,
  valueKeyStringForAxisY,
  getValuesForChartCb,
  chartTitle,
  chartSubTitle,
  subTitleTooltipText,
  shouldNormalize,
  isEngagementChart,
  dateEndPrefillArea,
  dateStartPrefillArea
}: LineChartControllerProps) => {
  const { selectedOrgSettings } = useSettings();
  const currentDate = new Date();
  const { socialProfiles, startDate, endDate, selectedProfilesIds } = useContext(AnalyticsContext);
  const profiles = useMemo(() => getValidProfiles(profileIds, socialProfiles), [profileIds, socialProfiles]) ;
  const dayStartOfWeek = selectedOrgSettings?.startOfWeekFormat ?? StartOfWeekFormatSetting.SUNDAY;
  const { hasPreFillingData, hasPostFillingData, hasFillingData } = getHasFillingDataProps(
    startDate,
    endDate,
    dayStartOfWeek
  );
  const chartValues = useMemo(() => (getValuesForChartCb ? getValuesForChartCb() : [[]]), [getValuesForChartCb]);
  const { maxValue: maxValueAxisY, minValue: minValueAxisY } = getMinMaxValue(chartValues, valueKeyStringForAxisY);
  const ticksAxisY = createTicksAxisY(minValueAxisY, maxValueAxisY);
  const fillingDataConfig = getFillingDataConfigs(
    firstDateFromData,
    lastDateFromData,
    startDate,
    endDate,
    ticksAxisY[0],
    ticksAxisY[ticksAxisY.length - 1],
    !!initialFollowers,
    hasFillingData,
    hasPreFillingData,
    hasPostFillingData,
    dateStartPrefillArea,
    dateEndPrefillArea
  );
  const ticksConfig = createTicksConfigs(startDate, endDate);

  const { chartDataObjs, dashedLinesPointsByProfile } = useMemo(
    () => createChartData(dates, profiles, chartValues, valueKeyString, shouldNormalize),
    [chartValues, dates, profiles, shouldNormalize, valueKeyString]
  );
  const tempTicks = useMemo(() => createTicksChart(dates), [dates] ) ;

  const ticks = useMemo(
    () =>
      verifyWhichTicksShouldRender(
        tempTicks,
        ticksConfig,
        hasPreFillingData,
        hasPostFillingData,
        true,
        startDate,
        dayStartOfWeek
      ),
    [tempTicks, ticksConfig, dayStartOfWeek, hasPostFillingData, hasPreFillingData, startDate]
  );
  const parsedFirstDate = parse(firstDateFromData, DateStringFormat, currentDate);
  const chartDomainAxisX = [
    parsedFirstDate.getTime(),
    parse(lastDateFromData, DateStringFormat, currentDate).getTime()
  ];
  const chartDomainAxisY = [ticksAxisY[0], ticksAxisY[ticksAxisY.length - 1]];
  // we need to make a fake values for the points for the pre/post fill data so the line is straight line without any peaks. Also connects the straight line with the dashed line
  const incompleteChartDataWithFakeValues = useMemo(() => makeFakePoints(
    chartDataObjs,
    profileIds,
    hasPreFillingData,
    hasPostFillingData,
    shouldNormalize
  ),[chartDataObjs, hasPostFillingData, hasPreFillingData, profileIds, shouldNormalize]); ;
  const incompleteChartDate = useMemo(
    () => addDashedLineChartsProps(incompleteChartDataWithFakeValues, dashedLinesPointsByProfile),
    [dashedLinesPointsByProfile, incompleteChartDataWithFakeValues]
  );
  const chartData = useMemo(() => addsPointOnPreviousDay(incompleteChartDate, dashedLinesPointsByProfile) ,[dashedLinesPointsByProfile, incompleteChartDate]) ;
  return (
    <LineChartView
      chartTitle={chartTitle}
      chartSubTitle={chartSubTitle}
      subTitleTooltipText={subTitleTooltipText}
      data={chartData}
      socialProfiles={profiles}
      ticksChart={ticks}
      hideGridLinesEveryFourLines={shouldHideGridLinesEveryFourLines(startDate, endDate)}
      hideGridLinesEverySevenLines={shouldHideGridLinesEverySevenLines(startDate, endDate)}
      fillingDataConfig={fillingDataConfig}
      chartDomainAxisX={chartDomainAxisX}
      chartDomainAxisY={chartDomainAxisY}
      loading={loading}
      isEmpty={isEmpty || selectedProfilesIds.length === 0}
      ticksConfig={ticksConfig}
      shouldNormalize={shouldNormalize}
      isEngagementChart={isEngagementChart}
      ticksAxisY={ticksAxisY}
    />
  );
};
